import NBType from '@netbase/nb-type'
import AMDomainStatSchema from './AMDomainStatSchema'
import AuthorSchema from './AuthorSchema'
import ContentType from '../enums/ContentType'
import FilterType from '../enums/FilterType'
import FirehoseStatus from '../enums/FirehoseStatus'
import GeoDisplaySchema from './GeoDisplaySchema'
import GeoFencingSchema from './GeoFencingSchema'
import InternalSectionSchema from './InternalSectionSchema'
import PostsSectionSchema from './PostsSectionSchema'
import Precision from '../enums/Precision'
import PremiumFeature from '../enums/PremiumFeature'
import ScopedStringListSchema from './ScopedStringListSchema'
import Sharing from '../enums/Sharing'
import SharingEditing from '../enums/SharingEditing'
import TimeInterval from '../enums/TimeInterval'
import TopicStatus from '../enums/TopicStatus'

/**
 * @typedef {import('./AMDomainStatSchema').AMDomainStat} AMDomainStat
 * @typedef {import('./AuthorSchema').Author} Author
 * @typedef {import('../enums/ContentType').ContentType} ContentType
 * @typedef {import('../enums/FilterType').FilterType} FilterType
 * @typedef {import('../enums/FirehoseStatus').FirehoseStatus} FirehoseStatus
 * @typedef {import('./GeoDisplaySchema').GeoDisplay} GeoDisplay
 * @typedef {import('./GeoFencingSchema').GeoFencing} GeoFencing
 * @typedef {import('./InternalSectionSchema').InternalSection} InternalSection
 * @typedef {import('./PostsSectionSchema').PostsSection} PostsSection
 * @typedef {import('../enums/Precision').Precision} Precision
 * @typedef {import('../enums/PremiumFeature').PremiumFeature} PremiumFeature
 * @typedef {import('./ScopedStringListSchema').ScopedStringList} ScopedStringList
 * @typedef {import('../enums/Sharing').Sharing} Sharing
 * @typedef {import('../enums/SharingEditing').SharingEditing} SharingEditing
 * @typedef {import('../enums/TimeInterval').TimeInterval} TimeInterval
 * @typedef {import('../enums/TopicStatus').TopicStatus} TopicStatus
 */

/**
 * @typedef {object} TopicSchema
 * @property {boolean} accessible
 * @property {number} accountProfileId
 * @property {boolean} accuracyScale
 * @property {boolean} active
 * @property {string[]} additionalContentIds
 * @property {boolean} alwaysActive
 * @property {AMDomainStat[]} amDomainStats
 * @property {boolean} annotated
 * @property {boolean} annotationEnabled
 * @property {boolean} archive
 * @property {boolean} bigBang
 * @property {string[]} brands
 * @property {number} buildProgress
 * @property {Author[]} channelExcludeAuthors
 * @property {ScopedStringList[]} channelExcludeTerms
 * @property {Author[]} channelIncludeAuthors
 * @property {ScopedStringList[]} channelIncludeTerms
 * @property {number} companyId
 * @property {ContentType} contentType
 * @property {number} createDate
 * @property {string[]} cvLogos
 * @property {Object} dataModelFeatures
 * @property {boolean} deleted
 * @property {string} description
 * @property {string} displayImgUrl
 * @property {number} downloadProgress
 * @property {Author[]} excludedAuthors
 * @property {string[]} excludedCountries
 * @property {GeoDisplay[]} excludedCountriesSummary
 * @property {string[]} excludedDomains
 * @property {string[][]} excludedMetaTags
 * @property {string[][]} excludedProductHierarchy
 * @property {ScopedStringList[]} excludedTerms
 * @property {number} expectedPrecision
 * @property {number} expectedRecall
 * @property {number} fhTwitterEndDate
 * @property {string} fhTwitterGMTStartDateString
 * @property {number} fhTwitterHistoryEndDate
 * @property {number} fhTwitterHistoryStartDate
 * @property {number} fhTwitterStartDate
 * @property {FirehoseStatus} fhTwitterStatus
 * @property {FilterType[]} filterTypes
 * @property {number} fromDate
 * @property {number} gaStartDate
 * @property {boolean} hasBeenAnnotated
 * @property {string} imgUrl
 * @property {Author[]} includedAuthors
 * @property {string[]} includedCountries
 * @property {GeoDisplay[]} includedCountriesSummary
 * @property {string[]} includedDomains
 * @property {string[][]} includedMetaTags
 * @property {string[][]} includedProductHierarchy
 * @property {ScopedStringList[]} includedTerms
 * @property {number} indexSampleFactor
 * @property {InternalSection} internalSection
 * @property {string[]} languageFilters
 * @property {number} lastDailyUpdateDate
 * @property {string} lastEditUserEmail
 * @property {number} lastEditUserProfileId
 * @property {string} lastEditUsername
 * @property {number} lastRunDate
 * @property {number} maxChannelSize
 * @property {number} maxConvergedMediaChannelSize
 * @property {number} maxDocCount
 * @property {number} maxFHA3DTopicAuthors
 * @property {number} maxPriority
 * @property {number} maxTweetsInMillion
 * @property {string} name
 * @property {string[]} negativeInsights
 * @property {string[]} neutralInsights
 * @property {number} numGeoFences
 * @property {string[]} ocr
 * @property {Object[]} ownedChannels
 * @property {string[]} ownedTwitterChannels
 * @property {string} owner
 * @property {Object[]} partneredChannels
 * @property {boolean} personalNarratives
 * @property {string[]} positiveInsights
 * @property {PostsSection} postsSection
 * @property {string[]} powerTags
 * @property {PremiumFeature[]} premiumFeatures
 * @property {boolean} reindexingInstall
 * @property {string[]} removedInsights
 * @property {boolean} runOnUpdate
 * @property {boolean} sampledGIIndex
 * @property {boolean} sampledIndex
 * @property {boolean} scale
 * @property {Precision} sentimentFocus
 * @property {Sharing} sharing
 * @property {SharingEditing} sharingEditing
 * @property {Object[]} socialWebChannels
 * @property {string[]} sources
 * @property {string[]} starRatingNames
 * @property {TopicStatus} status
 * @property {boolean} submittingAnnotation
 * @property {string[]} surveyScoreNames
 * @property {string[]} tags
 * @property {TimeInterval} timeInterval
 * @property {number} toDate
 * @property {GeoFencing[][]} topicGeoFencings
 * @property {number} topicId
 * @property {string} topicQuery
 * @property {number} updateDate
 * @property {boolean} usable
 * @property {number} userProfileId
 */
const TopicSchema = NBType.object().shape('TopicSchema', {
  /* @prop */
  accessible: NBType.boolean(),
  /* @prop */
  accountProfileId: NBType.number(),
  /* @prop */
  accuracyScale: NBType.boolean(),
  /* @prop */
  active: NBType.boolean(),
  /* @prop */
  additionalContentIds: NBType.array().of(NBType.string()),
  /* @prop */
  alwaysActive: NBType.boolean(),
  /* @prop */
  amDomainStats: NBType.array().of(AMDomainStatSchema),
  /* @prop */
  annotated: NBType.boolean(),
  /* @prop */
  annotationEnabled: NBType.boolean(),
  /* @prop */
  archive: NBType.boolean(),
  /* @prop */
  bigBang: NBType.boolean(),
  /* @prop */
  brands: NBType.array().of(NBType.string()),
  /* @prop */
  buildProgress: NBType.number(),
  /* @prop */
  channelExcludeAuthors: NBType.array().of(AuthorSchema),
  /* @prop */
  channelExcludeTerms: NBType.array().of(ScopedStringListSchema),
  /* @prop */
  channelIncludeAuthors: NBType.array().of(AuthorSchema),
  /* @prop */
  channelIncludeTerms: NBType.array().of(ScopedStringListSchema),
  /* @prop */
  companyId: NBType.number(),
  /* @prop */
  contentType: NBType.enum(ContentType),
  /* @prop */
  createDate: NBType.timestamp(),
  /* @prop */
  cvLogos: NBType.array().of(NBType.string()),
  /* @prop */
  dataModelFeatures: NBType.freeObject(),
  /* @prop */
  deleted: NBType.boolean(),
  /* @prop */
  description: NBType.string(),
  /* @prop */
  displayImgUrl: NBType.string(),
  /* @prop */
  downloadProgress: NBType.number(),
  /* @prop */
  excludedAuthors: NBType.array().of(AuthorSchema),
  /* @prop */
  excludedCountries: NBType.array().of(NBType.string()),
  /* @prop */
  excludedCountriesSummary: NBType.array().of(GeoDisplaySchema),
  /* @prop */
  excludedDomains: NBType.array().of(NBType.string()),
  /* @prop */
  excludedMetaTags: NBType.array().of(NBType.array().of(NBType.string())),
  /* @prop */
  excludedProductHierarchy: NBType.array().of(NBType.array().of(NBType.string())),
  /* @prop */
  excludedTerms: NBType.array().of(ScopedStringListSchema),
  /* @prop */
  expectedPrecision: NBType.number(),
  /* @prop */
  expectedRecall: NBType.number(),
  /* @prop */
  fhTwitterEndDate: NBType.timestamp(),
  /* @prop */
  fhTwitterGMTStartDateString: NBType.string(),
  /* @prop */
  fhTwitterHistoryEndDate: NBType.timestamp(),
  /* @prop */
  fhTwitterHistoryStartDate: NBType.timestamp(),
  /* @prop */
  fhTwitterStartDate: NBType.timestamp(),
  /* @prop */
  fhTwitterStatus: NBType.enum(FirehoseStatus),
  /* @prop */
  filterTypes: NBType.array().of(NBType.enum(FilterType)),
  /* @prop */
  fromDate: NBType.timestamp(),
  /* @prop */
  gaStartDate: NBType.timestamp(),
  /* @prop */
  hasBeenAnnotated: NBType.boolean(),
  /* @prop */
  imgUrl: NBType.string(),
  /* @prop */
  includedAuthors: NBType.array().of(AuthorSchema),
  /* @prop */
  includedCountries: NBType.array().of(NBType.string()),
  /* @prop */
  includedCountriesSummary: NBType.array().of(GeoDisplaySchema),
  /* @prop */
  includedDomains: NBType.array().of(NBType.string()),
  /* @prop */
  includedMetaTags: NBType.array().of(NBType.array().of(NBType.string())),
  /* @prop */
  includedProductHierarchy: NBType.array().of(NBType.array().of(NBType.string())),
  /* @prop */
  includedTerms: NBType.array().of(ScopedStringListSchema),
  /* @prop */
  indexSampleFactor: NBType.number(),
  /* @prop */
  internalSection: InternalSectionSchema,
  /* @prop */
  languageFilters: NBType.array().of(NBType.string()),
  /* @prop */
  lastDailyUpdateDate: NBType.timestamp(),
  /* @prop */
  lastEditUserEmail: NBType.string(),
  /* @prop */
  lastEditUserProfileId: NBType.number(),
  /* @prop */
  lastEditUsername: NBType.string(),
  /* @prop */
  lastRunDate: NBType.timestamp(),
  /* @prop */
  maxChannelSize: NBType.number(),
  /* @prop */
  maxConvergedMediaChannelSize: NBType.number(),
  /* @prop */
  maxDocCount: NBType.number(),
  /* @prop */
  maxFHA3DTopicAuthors: NBType.number(),
  /* @prop */
  maxPriority: NBType.number(),
  /* @prop */
  maxTweetsInMillion: NBType.number(),
  /* @prop */
  name: NBType.string(),
  /* @prop */
  negativeInsights: NBType.array().of(NBType.string()),
  /* @prop */
  neutralInsights: NBType.array().of(NBType.string()),
  /* @prop */
  numGeoFences: NBType.number(),
  /* @prop */
  ocr: NBType.array().of(NBType.string()),
  /* @prop */
  ownedChannels: NBType.array().of(NBType.freeObject()),
  /* @prop */
  ownedTwitterChannels: NBType.array().of(NBType.string()),
  /* @prop */
  owner: NBType.string(),
  /* @prop */
  partneredChannels: NBType.array().of(NBType.freeObject()),
  /* @prop */
  personalNarratives: NBType.boolean(),
  /* @prop */
  positiveInsights: NBType.array().of(NBType.string()),
  /* @prop */
  postsSection: PostsSectionSchema,
  /* @prop */
  powerTags: NBType.array().of(NBType.string()),
  /* @prop */
  premiumFeatures: NBType.array().of(NBType.enum(PremiumFeature)),
  /* @prop */
  reindexingInstall: NBType.boolean(),
  /* @prop */
  removedInsights: NBType.array().of(NBType.string()),
  /* @prop */
  runOnUpdate: NBType.boolean(),
  /* @prop */
  sampledGIIndex: NBType.boolean(),
  /* @prop */
  sampledIndex: NBType.boolean(),
  /* @prop */
  scale: NBType.boolean(),
  /* @prop */
  sentimentFocus: NBType.enum(Precision),
  /* @prop */
  sharing: NBType.enum(Sharing),
  /* @prop */
  sharingEditing: NBType.enum(SharingEditing),
  /* @prop */
  socialWebChannels: NBType.array().of(NBType.freeObject()),
  /* @prop */
  sources: NBType.array().of(NBType.string()),
  /* @prop */
  starRatingNames: NBType.array().of(NBType.string()),
  /* @prop */
  status: NBType.enum(TopicStatus),
  /* @prop */
  submittingAnnotation: NBType.boolean(),
  /* @prop */
  surveyScoreNames: NBType.array().of(NBType.string()),
  /* @prop */
  tags: NBType.array().of(NBType.string()),
  /* @prop */
  timeInterval: NBType.enum(TimeInterval),
  /* @prop */
  toDate: NBType.timestamp(),
  /* @prop */
  topicGeoFencings: NBType.array().of(NBType.array().of(GeoFencingSchema)),
  /* @prop */
  topicId: NBType.number(),
  /* @prop */
  topicQuery: NBType.string(),
  /* @prop */
  updateDate: NBType.timestamp(),
  /* @prop */
  usable: NBType.boolean(),
  /* @prop */
  userProfileId: NBType.number()
  /* END */
})
export default TopicSchema
// RESERVE BELOW
